import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  DeleteOutline,
  AddCircleOutline,
  WifiTethering,
  Close,
} from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";

import { APP_API_V1_BASE } from "../../../config/api";
import { useNotification } from "../../../context/NotificationContext";
// ─── Fonts ────────────────────────────────────────────────────────────────────
// <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Sora:wght@400;500;600;700&display=swap" rel="stylesheet" />

// ─── Theme Tokens ─────────────────────────────────────────────────────────────
const T = {
  navy: "#0E2748", // primary dark
  navyLight: "#1A3A63", // hover state for navy
  navyMuted: "#E8EDF5", // navy tint bg
  orange: "#F58A20", // primary accent
  orangeLight: "#FEF3E8", // orange tint bg
  orangeDark: "#D4720F", // orange hover
  orangeBorder: "#FBBF7A", // orange border tint
  white: "#FFFFFF",
  pageBg: "#F5F7FA", // slightly cool white
  border: "#E2E8F0", // default border
  borderHover: "#CBD5E1", // hover border
  textPrimary: "#0E2748", // navy = primary text
  textSec: "#5A6B82", // secondary text
  textMuted: "#94A3B8", // muted / placeholder
  disabledBg: "#F8FAFC",
  rowHover: "#F8FAFC",
};

// ─── SIDEBAR WIDTH ────────────────────────────────────────────────────────────
const SIDEBAR_WIDTH = 280;

// ─── Type Badge Colors ────────────────────────────────────────────────────────
const TYPE_COLORS = {
  Float: { bg: "#FEF3E8", color: "#C2620E", border: "#FBBF7A" },
  Integer: { bg: "#E8EDF5", color: "#1A3A63", border: "#93B4D8" },
  Boolean: { bg: "#ECFDF5", color: "#065F46", border: "#6EE7B7" },
  String: { bg: "#F5F3FF", color: "#5B21B6", border: "#C4B5FD" },
};

const DATA_TYPES = ["Float", "Integer", "String", "Boolean", "Custom"];

const UNIT_TYPES = [
  "-",
  "dBm",
  "dB",
  "m",
  "%",
  "°C",
  "mg/kg",
  "NTU",
  "kWh",
  "L/min",
  "ppm",
  "dS/m",
  "pH",
  "V",
  "W",
  "A",
  "lx",
  "Custom",
];

// ─── Field Styles ─────────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    borderRadius: "8px",
    background: T.white,
    color: T.textPrimary,
    "& fieldset": { borderColor: T.border },
    "&:hover fieldset": { borderColor: T.borderHover },
    "&.Mui-focused fieldset": {
      borderColor: T.orange,
      borderWidth: "1.5px",
    },
    "&.Mui-disabled": { background: T.disabledBg },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: T.border,
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    color: T.textMuted,
    "&.Mui-focused": { color: T.orange },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 12,
    WebkitTextFillColor: T.textMuted,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 11,
    color: T.textMuted,
    mt: 0.5,
  },
};

const schemaInputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    borderRadius: "7px",
    color: T.textPrimary,
    "& fieldset": { borderColor: T.border },
    "&:hover fieldset": { borderColor: T.borderHover },
    "&.Mui-focused fieldset": {
      borderColor: T.orange,
      borderWidth: "1.5px",
    },
    "&.Mui-disabled": { background: T.disabledBg },
  },
  "& .MuiSelect-icon": { color: T.textSec },
};

// ─── API ──────────────────────────────────────────────────────────────────────
export const getDevice = async (deviceId) => {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("Auth token missing");
  try {
    const { data } = await axios.get(`${APP_API_V1_BASE}/devices/${deviceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    if (error.response)
      throw new Error(
        error.response.data?.error ||
          error.response.data?.message ||
          "Failed to fetch device",
      );
    if (error.request) throw new Error("Network error. Please try again.");
    throw error;
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: "'Sora', sans-serif",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#fff",
      }}
    >
      {children}
    </Typography>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: "'Sora', sans-serif",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: T.textMuted,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] || TYPE_COLORS.String;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        fontWeight: 600,
        px: 0.75,
        py: 0.3,
        borderRadius: "4px",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        verticalAlign: "middle",
      }}
    >
      {type}
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EditDeviceDetails() {
  const navigate = useNavigate();
  const { notification, showNotification, closeNotification } =
    useNotification();
  const { state } = useLocation();
  const { deviceId } = useParams();

  const device = state?.device;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currDeviceTypes, setCurrDeviceTypes] = useState(null);
  const [deviceTypes, setDeviceTypes] = useState(null);
  const [name, setName] = useState(device?.name || "");
  const [protocol] = useState(device?.protocol || "MQTT v3.1.1");
  const [schema, setSchema] = useState(
    device?.schema?.length
      ? device.schema.map((item) => ({
          key: item.key || "",
          type: item.type || "",
          customType: item.customType || "",
          unit: item.unit || "",
          customUnit: item.customUnit || "",
        }))
      : [{ key: "", type: "", customType: "", unit: "", customUnit: "" }],
  );
  const [loading, setLoading] = useState(false);
  // const [toast, setToast] = useState({
  //   open: false,
  //   message: "",
  //   severity: "success",
  // });

  useEffect(() => {
    if (state?.device_type) setDeviceTypes(state.device_type);
  }, [state]);

  useEffect(() => {
    if (!deviceId) return;
    (async () => {
      try {
        const data = await getDevice(deviceId);
        setCurrDeviceTypes(data.device_type_id);
      } catch (err) {
        showNotification(err.message, "error");
      }
    })();
  }, [deviceId]);

  // const showToast = (message, severity = "success") =>
  //   setToast({ open: true, message, severity });

  const updateSchemaRow = (index, field, value) => {
    setSchema((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };

        // Clear customType when type is not "Custom"
        if (field === "type" && value !== "Custom") {
          updated.customType = "";
        }

        // Clear customUnit when unit is not "Custom"
        if (field === "unit" && value !== "Custom") {
          updated.customUnit = "";
        }

        return updated;
      }),
    );
  };

  const addSchemaRow = () =>
    setSchema((prev) => [
      ...prev,
      { key: "", type: "", customType: "", unit: "", customUnit: "" },
    ]);

  const deleteSchemaRow = (index) => {
    if (schema.length === 1) {
      setSchema([
        { key: "", type: "", customType: "", unit: "", customUnit: "" },
      ]);
      return;
    }
    setSchema((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    setConfirmOpen(false);
    const token = localStorage.getItem("authToken");
    if (!token) {
      showNotification("Auth token missing", "error");
      setLoading(false);
      return;
    }
    try {
      await axios.patch(
        `${APP_API_V1_BASE}/devices/${deviceId}`,
        { name, schema, device_type_id: currDeviceTypes },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      showNotification("Device updated successfully");
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      showNotification(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to update device.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const filledSchemaCount = schema.filter(
    (row) =>
      String(row?.key || "").trim() &&
      String(row?.type || "").trim() &&
      String(row?.unit || "").trim(),
  ).length;
  const customSchemaCount = schema.filter(
    (row) => row?.type === "Custom" || row?.unit === "Custom",
  ).length;
 
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
        fontFamily: "'Sora', sans-serif",
      }}
    >
      {/* ══════════════════════════════ CONTENT ══════════════════════════════════ */}
      <Box
        sx={{
          maxWidth: 960,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 6 },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {/* ── Navbar Box Card ─────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2.5 },
              py: { xs: 1.25, md: 1.75 },
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: { xs: 56, md: 64 },
              gap: { xs: 1, md: 0 },
            }}
          >
            {/* LEFT */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 1.5 },
                minWidth: 0,
                flex: 1,
              }}
            >
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  borderRadius: "8px",
                  p: "6px",
                  flexShrink: 0,
                  "&:hover": {
                    background: "rgba(255,255,255,0.10)",
                    color: "#ffffff",
                  },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                {/* Title row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 3,
                      height: { xs: 16, md: 20 },
                      borderRadius: "2px",
                      background: "#FB923C",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Sora', sans-serif",
                      fontSize: { xs: 14, sm: 18, md: 21 },
                      fontWeight: 700,
                      color: "#ffffff",
                      letterSpacing: "-0.4px",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Device Updates
                  </Typography>
                </Box>

                {/* Subtitle */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.3,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#34D399",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: { xs: 9, md: 11 },
                      color: "rgba(255,255,255,0.60)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { xs: 80, sm: 220, md: "none" },
                    }}
                  >
                    {name || device?.name || "Unnamed Device"}
                  </Typography>
                  {/* Hide chip on xs, show from sm */}
                  <Chip
                    label={deviceId}
                    size="small"
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: { xs: 9, md: 10 },
                      height: { xs: 17, md: 19 },
                      background: "rgba(255,255,255,0.10)",
                      color: "rgba(255,255,255,0.85)",
                      border: "1px solid rgba(255,255,255,0.20)",
                      borderRadius: "4px",
                      flexShrink: 0,
                      display: { xs: "none", sm: "inline-flex" },
                      ".MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* RIGHT: Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0.75, md: 1.5 },
                flexShrink: 0,
                ml: { xs: 1, md: 2 },
              }}
            >
              {/* Cancel — icon only on xs, text from sm */}
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontSize: { xs: 11, md: 13 },
                  fontWeight: 500,
                  px: { xs: 1, md: 2.5 },
                  py: { xs: 0.75, md: 1 },
                  minWidth: { xs: 0, md: "auto" },
                  borderRadius: "8px",
                  borderColor: "rgba(255,255,255,0.30)",
                  color: "rgba(255,255,255,0.80)",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  display: { xs: "none", sm: "inline-flex" },
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.70)",
                    color: "#ffffff",
                    background: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                Cancel
              </Button>

              {/* Cancel icon-only for xs */}
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  color: "rgba(255,255,255,0.75)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: "8px",
                  p: "6px",
                  "&:hover": {
                    background: "rgba(255,255,255,0.08)",
                    color: "#ffffff",
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>

              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontSize: { xs: 11, md: 13 },
                  fontWeight: 700,
                  px: { xs: 1.25, md: 2.5 },
                  py: { xs: 0.75, md: 1 },
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  color: "#1a1a1a",
                  textTransform: "none",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    background: "linear-gradient(135deg, #FDE68A, #d5911b)",
                    boxShadow: "0 0 0 3px rgba(249,115,22,0.25)",
                  },
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                {/* Short label on xs */}
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Update Device
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "inline", sm: "none" } }}
                >
                  Update
                </Box>
              </Button>
            </Box>
          </Box>
        </Paper>
        {/* ── Device Information Card ─────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${T.border}`,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
          }}
        >
          {/* Card header — navy accent */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid rgba(148, 163, 184, 0.18)",
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            }}
          >
            <SectionLabel>Device Information</SectionLabel>
          </Box>

          {/* Card body */}
          <Box sx={{ p: { xs: 2, md: 2.5 }, background: T.white }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2.5,
              }}
            >
              <Box>
                <FieldLabel>Device ID</FieldLabel>
                <TextField
                  value={deviceId}
                  disabled
                  fullWidth
                  size="small"
                  helperText="Read-only identifier"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <FieldLabel>Device Name</FieldLabel>
                <TextField
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter device name"
                  helperText="Editable display name"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <FieldLabel>Protocol</FieldLabel>
                <TextField
                  value={protocol}
                  disabled
                  fullWidth
                  size="small"
                  helperText="Communication protocol"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <FieldLabel>Device Type</FieldLabel>
                <TextField
                  select
                  value={currDeviceTypes || ""}
                  onChange={(e) => setCurrDeviceTypes(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Select device category"
                  sx={fieldSx}
                >
                  {deviceTypes?.map((type) => (
                    <MenuItem
                      key={type.id}
                      value={type.id}
                      sx={{
                        fontFamily: "'Sora', sans-serif",
                        fontSize: 13,
                        color: T.textPrimary,
                      }}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* ── Schema Editor Card ──────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${T.border}`,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Card header — orange accent */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid rgba(148, 163, 184, 0.18)",
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 3,
                  height: 16,
                  borderRadius: "2px",
                  background: T.orange,
                }}
              />
              <SectionLabel>Telemetry Schema</SectionLabel>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Chip
                size="small"
                label={`${schema.length} rows`}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#DBEAFE",
                  background: "rgba(59,130,246,0.22)",
                  border: "1px solid rgba(147,197,253,0.4)",
                }}
              />
              <Chip
                size="small"
                label={`${filledSchemaCount} configured`}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#D1FAE5",
                  background: "rgba(16,185,129,0.22)",
                  border: "1px solid rgba(110,231,183,0.4)",
                }}
              />
              {customSchemaCount > 0 && (
                <Chip
                  size="small"
                  label={`${customSchemaCount} custom`}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#FDE68A",
                    background: "rgba(245,158,11,0.2)",
                    border: "1px solid rgba(251,191,36,0.35)",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Schema Rows */}
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {schema.map((row, index) => {
                const hasCustomType = row.type === "Custom";
                const hasCustomUnit = row.unit === "Custom";
                const desktopColumns = [
                  "1fr",
                  "0.62fr",
                  ...(hasCustomType ? ["0.62fr"] : []),
                  "0.52fr",
                  ...(hasCustomUnit ? ["0.62fr"] : []),
                  "auto",
                ].join(" ");
                const unitColumn = hasCustomType ? "4 / 5" : "3 / 4";
                const customUnitColumn = hasCustomType ? "5 / 6" : "4 / 5";
                const deleteColumn = hasCustomUnit
                  ? hasCustomType
                    ? "6 / 7"
                    : "5 / 6"
                  : hasCustomType
                    ? "5 / 6"
                    : "4 / 5";

                return (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      border: "1px solid rgba(203, 213, 225, 0.9)",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(251,146,60,0.5)",
                        boxShadow: "0 12px 28px rgba(14, 39, 72, 0.08)",
                      },
                    }}
                  >
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={`ROW ${String(index + 1).padStart(2, "0")}`}
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontWeight: 700,
                          color: "#9A3412",
                          background: "#FFEDD5",
                          border: "1px solid #FDBA74",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: T.textSec,
                          fontWeight: 600,
                        }}
                      >
                        Telemetry field mapping
                      </Typography>
                    </Box>
                    {row.type && row.type !== "Custom" && <TypeBadge type={row.type} />}
                  </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: desktopColumns,
                        },
                        gap: 1.25,
                        alignItems: "start",
                      }}
                    >
                    {/* Key */}
                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", md: "1 / 2" },
                        gridRow: { md: "1 / 2" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: T.textMuted,
                          fontWeight: 700,
                          mb: 0.75,
                          textTransform: "uppercase",
                        }}
                      >
                        Key
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.key}
                        onChange={(e) =>
                          updateSchemaRow(index, "key", e.target.value)
                        }
                        placeholder="temperature, humidity..."
                        sx={schemaInputSx}
                        inputProps={{
                          style: {
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 13,
                          },
                        }}
                      />
                    </Box>

                    {/* Data Type */}
                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", md: "2 / 3" },
                        gridRow: { md: "1 / 2" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: T.textMuted,
                          fontWeight: 700,
                          mb: 0.75,
                          textTransform: "uppercase",
                        }}
                      >
                        Data Type
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.type}
                        onChange={(e) =>
                          updateSchemaRow(index, "type", e.target.value)
                        }
                        sx={schemaInputSx}
                      >
                        {DATA_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    {/* Custom Type (conditional) */}
                    {row.type === "Custom" && (
                      <Box
                        sx={{
                          gridColumn: { xs: "1 / -1", md: "3 / 4" },
                          gridRow: { md: "1 / 2" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: T.textMuted,
                            fontWeight: 700,
                            mb: 0.75,
                            textTransform: "uppercase",
                          }}
                        >
                          Custom Type
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={row.customType}
                          onChange={(e) =>
                            updateSchemaRow(index, "customType", e.target.value)
                          }
                          // placeholder="Enter custom type"
                          sx={schemaInputSx}
                        />
                      </Box>
                    )}

                    {/* Unit */}
                    <Box
                      sx={{
                          gridColumn: { xs: "1 / -1", md: unitColumn },
                          gridRow: { md: "1 / 2" },
                        }}
                      >
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: T.textMuted,
                          fontWeight: 700,
                          mb: 0.75,
                          textTransform: "uppercase",
                        }}
                      >
                        Unit
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.unit}
                        onChange={(e) =>
                          updateSchemaRow(index, "unit", e.target.value)
                        }
                        sx={schemaInputSx}
                      >
                        {UNIT_TYPES.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    {/* Custom Unit (conditional) */}
                    {row.unit === "Custom" && (
                      <Box
                        sx={{
                          gridColumn: { xs: "1 / -1", md: customUnitColumn },
                          gridRow: { md: "1 / 2" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: T.textMuted,
                            fontWeight: 700,
                            mb: 0.75,
                            textTransform: "uppercase",
                          }}
                        >
                          Custom Unit
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={row.customUnit}
                          onChange={(e) =>
                            updateSchemaRow(index, "customUnit", e.target.value)
                          }
                          // placeholder="Enter custom unit"
                          sx={schemaInputSx}
                        />
                      </Box>
                    )}

                    {/* Delete Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "flex-end", md: "center" },
                        gridColumn: { xs: "1 / -1", md: deleteColumn },
                        gridRow: { md: "1 / 2" },
                        height: "100%",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => deleteSchemaRow(index)}
                        disabled={schema.length === 1}
                        sx={{
                          color: "#64748B",
                          borderRadius: "10px",
                          border: "1px solid rgba(203,213,225,0.9)",
                          background: "#FFFFFF",
                          "&:hover": {
                            background: "#FEF2F2",
                            color: "#DC2626",
                            borderColor: "#FCA5A5",
                          },
                          "&:disabled": { opacity: 0.3 },
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Add Button */}
            <Button
              startIcon={
                <AddCircleOutline sx={{ fontSize: "15px !important" }} />
              }
              onClick={addSchemaRow}
              sx={{
                mt: 2.25,
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 700,
                color: "#9A3412",
                textTransform: "none",
                borderRadius: "12px",
                px: 2,
                py: 1,
                border: "1px dashed #FB923C",
                background: "linear-gradient(180deg, #FFF7ED, #FFEDD5)",
                "&:hover": {
                  border: "1px solid #EA580C",
                  background: "linear-gradient(180deg, #FFEDD5, #FED7AA)",
                },
              }}
            >
              Add telemetry row
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* ══════════════════════════════ CONFIRM DIALOG ═══════════════════════════ */}
      <Dialog
        open={confirmOpen}
        onClose={!loading ? () => setConfirmOpen(false) : undefined}
        PaperProps={{
          sx: {
            fontFamily: "'Sora', sans-serif",
            borderRadius: "14px",
            border: `1px solid ${T.border}`,
            boxShadow: "0 24px 64px rgba(14,39,72,0.14)",
            width: { xs: "90vw", sm: 420 },
            maxWidth: 420,
            overflow: "hidden",
          },
        }}
      >
        {/* Dialog top accent bar */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${T.navy} 0%, ${T.orange} 100%)`,
          }}
        />

        <DialogTitle
          sx={{
            fontFamily: "'Sora', sans-serif",
            fontSize: 15,
            fontWeight: 700,
            color: T.navy,
            borderBottom: `1px solid ${T.border}`,
            py: 2,
            px: 2.5,
          }}
        >
          Confirm Device Update
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Typography
            sx={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 13,
              color: T.textSec,
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to update this device? Changes to the
            telemetry schema may affect active data pipelines.
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              background: T.navyMuted,
              borderRadius: "8px",
              border: `1px solid #C4D0E3`,
            }}
          >
            {[
              { label: "Device", value: name || "—" },
              { label: "ID", value: deviceId || "—" },
              {
                label: "Schema",
                value: `${schema.length} field${schema.length !== 1 ? "s" : ""}`,
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 12,
                    color: T.textSec,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12,
                    color: T.navy,
                    fontWeight: 600,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 2.5, py: 2, gap: 1, borderTop: `1px solid ${T.border}` }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
            sx={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              color: T.textSec,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              border: `1px solid ${T.border}`,
              "&:hover": {
                background: T.navyMuted,
                borderColor: T.navy,
                color: T.navy,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              minWidth: 140,
              background: T.orange,
              boxShadow: "none",
              "&:hover": { background: T.orangeDark, boxShadow: "none" },
              "&:disabled": { background: T.orangeBorder, color: T.white },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: T.white }} />
                <span>Saving...</span>
              </Box>
            ) : (
              "Yes, Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════ TOAST ════════════════════════════════════ */}
      <Snackbar
  open={notification.open}
  autoHideDuration={3500}
  onClose={closeNotification}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert
    severity={notification.severity}
    variant="filled"
    onClose={closeNotification}
    sx={{
      fontFamily: "'Sora', sans-serif",
      fontSize: 13,
      borderRadius: "8px",
    }}
  >
    {notification.message}
  </Alert>
</Snackbar>
    </Box>
  );
}
